import { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";

interface FavoritesContextType {
  favorites: Set<string>;
  toggleFavorite: (productId: string) => void;
  isFavorited: (productId: string) => boolean;
  loading: boolean;
  count: number;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

const FAV_KEY_PREFIX = "store_favorites_";

function getFavKey(tenantId: string | null): string {
  return tenantId ? `${FAV_KEY_PREFIX}${tenantId}` : "store_favorites";
}

function loadLocal(tenantId: string | null): string[] {
  try { return JSON.parse(localStorage.getItem(getFavKey(tenantId)) || "[]"); } catch { return []; }
}
function saveLocal(ids: string[], tenantId: string | null) {
  localStorage.setItem(getFavKey(tenantId), JSON.stringify(ids));
}

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [synced, setSynced] = useState(false);

  // Re-load from localStorage when tenantId changes
  useEffect(() => {
    setFavorites(new Set(loadLocal(tenantId)));
  }, [tenantId]);

  // Load from DB when user logs in
  useEffect(() => {
    if (!user || !tenantId) {
      setSynced(false);
      // Validate localStorage favorites for anonymous users
      const localIds = loadLocal(tenantId);
      if (localIds.length > 0 && tenantId) {
        supabase.from("products").select("id").in("id", localIds).then(({ data }) => {
          const validIds = (data ?? []).map(p => p.id);
          setFavorites(new Set(validIds));
          saveLocal(validIds, tenantId);
        });
      }
      return;
    }
    setLoading(true);
    const localIds = loadLocal(tenantId);

    (async () => {
      // Fetch existing DB favorites for this tenant
      const { data: dbFavs } = await supabase
        .from("favorites")
        .select("product_id")
        .eq("user_id", user.id)
        .eq("tenant_id", tenantId);

      const dbIds = new Set((dbFavs ?? []).map(f => f.product_id));

      // Merge: local items not yet in DB get inserted
      const toInsert = localIds.filter(id => !dbIds.has(id));
      if (toInsert.length > 0) {
        await supabase.from("favorites").insert(
          toInsert.map(product_id => ({ user_id: user.id, product_id, tenant_id: tenantId }))
        );
        toInsert.forEach(id => dbIds.add(id));
      }

      // Validate: remove favorites for products that no longer exist
      if (dbIds.size > 0) {
        const { data: validProducts } = await supabase
          .from("products")
          .select("id")
          .in("id", [...dbIds]);

        const validIds = new Set((validProducts ?? []).map(p => p.id));
        const orphaned = [...dbIds].filter(id => !validIds.has(id));

        if (orphaned.length > 0) {
          for (const pid of orphaned) {
            supabase.from("favorites").delete()
              .eq("user_id", user.id)
              .eq("product_id", pid)
              .then(() => {});
          }
          orphaned.forEach(id => dbIds.delete(id));
        }
      }

      setFavorites(dbIds);
      saveLocal([...dbIds], tenantId);
      setSynced(true);
      setLoading(false);
    })();
  }, [user, tenantId]);

  const toggleFavorite = useCallback((productId: string) => {
    setFavorites(prev => {
      const next = new Set(prev);
      const removing = next.has(productId);

      if (removing) {
        next.delete(productId);
      } else {
        next.add(productId);
      }

      saveLocal([...next], tenantId);

      // Sync to DB if logged in
      if (user && tenantId) {
        if (removing) {
          supabase.from("favorites").delete()
            .eq("user_id", user.id)
            .eq("product_id", productId)
            .then(() => {});
        } else {
          supabase.from("favorites").insert({ user_id: user.id, product_id: productId, tenant_id: tenantId })
            .then(() => {});
        }
      }

      return next;
    });
  }, [user, tenantId]);

  const isFavorited = useCallback((productId: string) => favorites.has(productId), [favorites]);

  const value = useMemo<FavoritesContextType>(() => ({
    favorites,
    toggleFavorite,
    isFavorited,
    loading,
    count: favorites.size,
  }), [favorites, toggleFavorite, isFavorited, loading]);

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used within FavoritesProvider");
  return ctx;
}
